<template>
  <v-sheet>
    <h4 v-if="title">
      <v-icon v-if="icon" :color="list.length ? iconActive : null"
        >mdi-{{ icon }}</v-icon
      >
      {{ title }}
    </h4>
    <v-row class="mt-1 mx-0" v-if="fields.length">
      <v-col v-for="(field, index) in fields" :key="index" class="pa-1">
        <v-text-field
          v-if="field.type == 'text'"
          v-model="line[field.name]"
          :label="field.label"
          hide-details
          :disabled="deactivated"
          :outlined="outlined"
          @keyup.enter="add"
          clearable
          dense
        />
        <v-file-input
          v-if="field.type == 'file'"
          v-model="line[field.name]"
          :label="field.label"
          hide-details
          :disabled="deactivated"
          :outlined="outlined"
          @keyup.enter="add"
          @change="log"
          clearable
          dense
        />
        <v-autocomplete
          v-if="field.type == 'list'"
          v-model="line[field.name]"
          :items="field.items"
          :label="field.label"
          :outlined="outlined"
          hide-details
          :disabled="deactivated"
          clearable
          :hide-selected="field.hideSelected"
          :chips="field.chips"
          :small="field.small"
          @keyup.enter="add"
          @update:list-index="add"
          @click="add"
          dense
        />
      </v-col>
      <v-col cols="auto">
        <v-icon
          @click="add"
          :color="valid ? 'primary' : null"
          :disabled="deactivated || !valid"
        >
          mdi-{{ addIcon }}
        </v-icon>
      </v-col>
    </v-row>
    <v-list
      class="scrollable hm15 mt-3"
      outlined
      dropzone
      v-if="list.length && !chipsMod"
    >
      <v-list-item
        v-for="(item, index) in list"
        :key="item + '-' + index"
        dense
        containment="parent"
        :draggable="draggable && !deactivated"
        @dragstart="drag(index)"
        @dragover="over(index)"
        @dragend="drop(item)"
        :class="{
          listItemDrag: true,
          dragged: index === dragged,
          over: index === overCurrent && index !== dragged,
        }"
      >
        <v-list-item-content>
          <v-list-item-title>
            <strong v-if="!objMod">{{ item }}</strong>
            <v-row v-else class="no-gutters">
              <v-col cols="auto">
                <v-icon class="pa-0 mt-2">mdi-{{ itemIcon }}</v-icon>
              </v-col>
              <v-col
                v-for="(field, index) in fields"
                :key="index"
                class="mt-1 ml-1"
              >
                <v-file-input
                  v-if="field.type == 'file'"
                  v-model="item[field.name]"
                  :label="field.label"
                  :disabled="deactivated || field.disabled"
                  @input="changed"
                  @change="changed"
                  :outlined="outlined || field.outlined"
                  hide-details
                  dense
                />
                <v-text-field
                  v-if="field.type == 'text'"
                  v-model="item[field.name]"
                  :label="field.label"
                  :disabled="deactivated || field.disabled"
                  @input="changed"
                  @change="changed"
                  :outlined="outlined || field.outlined"
                  hide-details
                  dense
                />
                <v-autocomplete
                  v-if="field.type == 'list'"
                  v-model="item[field.name]"
                  :items="field.items"
                  :label="field.label"
                  :outlined="outlined"
                  hide-details
                  :disabled="deactivated"
                  clearable
                  :hide-selected="field.hideSelected"
                  :chips="field.chips"
                  :small="field.small"
                  @keyup.enter="add"
                  @update:list-index="add"
                  @click="add"
                  dense
                />
              </v-col>
            </v-row>
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <Tooltip :content="deleteMsg" color="error" top>
            <v-btn icon small @click="remove(index)" :disabled="deactivated">
              <v-icon small color="error lighten-1">
                mdi-{{ deleteIcon }}
              </v-icon>
            </v-btn>
          </Tooltip>
        </v-list-item-action>
      </v-list-item>
    </v-list>
    <v-sheet
      class="scrollable hm15 mt-3"
      v-if="list.length && chipsMod"
      outlined
    >
      <v-chip-group column class="pa-1">
        <v-chip
          v-for="(item, index) in list"
          :key="index"
          close
          small
          :close-icon="'mdi-' + deleteIcon"
          :close-label="deleteMsg"
          @click:close="remove(index)"
        >
          {{ item }}
        </v-chip>
      </v-chip-group>
    </v-sheet>
    <v-row v-if="!list.length">
      <v-col class="text-center" v-if="emptyMsg">
        <v-icon small>mdi-cancel</v-icon>
        {{ emptyMsg }}
      </v-col>
    </v-row>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import VueTypes from "vue-types";
import Tooltip from "@/components/Tooltip.vue";

export default Vue.extend({
  name: "AddList",
  data() {
    return {
      line: {},
      dragged: null as unknown,
      overCurrent: null as unknown,
    };
  },
  props: {
    value: VueTypes.any.def([]),
    fields: VueTypes.any.def([
      {
        type: "text",
        name: "field",
        label: "Field",
      },
    ]),
    changedRef: VueTypes.string.def(""),
    title: VueTypes.string.def(""),
    icon: VueTypes.string.def(""),
    iconActive: VueTypes.string.def("info"),
    addIcon: VueTypes.string.def("plus-circle"),
    itemIcon: VueTypes.string.def("drag-vertical"),
    deleteIcon: VueTypes.string.def("delete"),
    deleteMsg: VueTypes.string.def("Delete"),
    emptyMsg: VueTypes.string.def(""),
    disabled: VueTypes.bool.def(false),
    draggable: VueTypes.bool.def(true),
    outlined: VueTypes.bool.def(false),
    objList: VueTypes.bool.def(false),
    chips: VueTypes.bool.def(false),
  },
  computed: {
    list: {
      get() {
        return this.value;
      },
      set(val: unknown[]) {
        this.$emit("input", val);
      },
    },
    valid() {
      for (const field of this.fields) {
        // console.log(this.line[field.name].length);

        if (!this.line[field.name]?.length && !this.line[field.name]?.size) return false;
      }
      return true;
    },
    objMod(): boolean {
      return this.fields?.length > 1 || this.objList;
    },
    chipsMod(): boolean {
      return !this.objMod && this.chips;
    },
    deactivated(): boolean {
      return this.disabled;
    },
  },
  methods: {
    add() {
      if (this.valid) {
        console.log(this.line);

        if (this.objMod) {
          this.list.push({ ...this.line });
        } else {
          this.list.push(...Object.values(this.line));
        }
        // clean the line values
        for (const key of Object.keys(this.line)) {
          this.line[key] = "";
        }
        this.changed();
      }
    },
    log() {
      console.log(this.line);
    },
    remove(i: number) {
      if (this.list.length >= i + 1) {
        this.list.splice(i, 1);
      }
      this.changed();
    },
    drag(i: number) {
      if (this.draggable) {
        this.dragged = i;
      }
    },
    over(i: number) {
      if (i !== this.overCurrent) {
        this.overCurrent = i;
      }
    },
    drop(item: unknown) {
      if (this.draggable) {
        this.list.splice(this.dragged, 1);
        this.list.splice(this.overCurrent, 0, item);
        this.overCurrent = null;
        this.dragged = null;
        this.changed();
      }
    },
    changed() {
      if (this.changedRef) {
        this.$emit("changed", { [this.changedRef]: this.list });
      } else {
        this.$emit("changed", this.list);
      }
      this.$emit("input", this.list);
    },
  },
  components: { Tooltip },
});
</script>
<style scoped>
.timeFrame {
  color: teal;
  font-weight: normal;
}
input[type="number"] {
  text-align: center !important;
}
.listItemDrag {
  cursor: move;
}
.dragged {
  opacity: 0.2 !important;
}
.over {
  background-color: rgba(204, 204, 204, 0.308) !important;
}
</style>
